import React, { useEffect, useRef, useState } from 'react';

import Monitor from './Monitor.js';

const HEIGHT = 150;

const PulseMonitor = ({
  label,
  className,
  color,
  sample,
  readout,
  initialValue,
  valueId,
  unit,
  renderReadout,
  limits,
  sampleStep = 1,
}) => {
  const canvasRef = useRef(null);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return undefined;
    const monitor = new Monitor({ sample, color, sampleStep });
    let width;
    let animationFrame;
    let readoutSecond = -1;

    const resize = () => {
      const bounds = canvas.parentElement.getBoundingClientRect();
      width = Math.max(1, Math.floor(bounds.width));
      const height = Math.max(1, Math.floor(bounds.height));
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.parentElement.parentElement.style.setProperty('--monitor-row-height', `${height}px`);
      // Preserve the entire 150-unit waveform when the available row is shorter.
      context.setTransform(pixelRatio, 0, 0, (pixelRatio * height) / HEIGHT, 0, 0);
    };

    const draw = (timestamp) => {
      const seconds = timestamp / 1000;
      monitor.plot(context, width, HEIGHT, seconds);
      if (Math.floor(seconds) !== readoutSecond) {
        readoutSecond = Math.floor(seconds);
        setValue(readout(seconds));
      }
      animationFrame = window.requestAnimationFrame(draw);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas.parentElement);
    animationFrame = window.requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [color, sample, readout, sampleStep]);

  return (
    <div
      className={`monitor-row ${className}`}
      style={{ '--monitor-color': `rgb(${color.join(',')})` }}
    >
      <div className="monitor-waveform">
        <canvas
          ref={canvasRef}
          width={120}
          height={HEIGHT}
          aria-label={`${label} waveform, simulated healthy resting adult`}
          style={{ display: 'block' }}
        />
        <span className="monitor-waveform-label">{label}</span>
      </div>
      <div className="monitor-readout">
        {renderReadout ? (
          renderReadout(value)
        ) : (
          <>
            <span className="monitor-readout-label">{unit}</span>
            <div className="monitor-value-row">
              {limits && (
                <span
                  className="monitor-limits"
                  aria-label={`Display limits ${limits[0]} to ${limits[1]}`}
                >
                  <span>{limits[1]}</span>
                  <span>{limits[0]}</span>
                </span>
              )}
              <span id={valueId} className="monitor-numeric">
                {Math.round(value)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PulseMonitor;
